import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';


/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  #legacyShellObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#scheduleLegacyShellCleanup();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#legacyShellObserver?.disconnect();
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #scheduleLegacyShellCleanup() {
    if (!document.body.classList.contains('template-collection')) return;

    const runCleanup = () => this.#cleanupLegacyCollectionShell();

    runCleanup();
    requestAnimationFrame(runCleanup);
    window.setTimeout(runCleanup, 250);
    window.setTimeout(runCleanup, 1500);

    this.#legacyShellObserver?.disconnect();
    this.#legacyShellObserver = new MutationObserver(() => {
      runCleanup();
    });
    this.#legacyShellObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => this.#legacyShellObserver?.disconnect(), 4000);
  }

  #cleanupLegacyCollectionShell() {
    if (!document.body.classList.contains('template-collection')) return;

    document.querySelector('.collection-editorial')?.remove();
    document.querySelector('.collection-page-shell__eyebrow')?.remove();
    document.querySelectorAll('.collection-context').forEach((node) => {
      node.closest('.section, .section-wrapper, [id^="shopify-section"]')?.remove();
    });
    document.querySelectorAll('.collection-context__group-heading, .collection-context__cta').forEach((node) => {
      node.closest('.section, .section-wrapper, [id^="shopify-section"]')?.remove();
    });
  }

}


if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
